import { Grid, Box, useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { maxDealerValuations } from '../../../../../server/schema/constants';
import { useCurrentUser } from '../../../../Session';
import {
    DealerQuotesStatus,
    FullPurchaseTransactionDataFragment,
    GetPurchaseTransactionQueryResult,
    GetQuotationQueryResult,
    QuotationUpdatedSubscription,
    useGetPurchaseTransactionLiveDisplayQuery,
    useGetQuotationQuery,
    UserType,
} from '../../../../api';
import TabPanel from '../../../../components/TabPanel';
import useDidUpdateEffect from '../../../../utilities/useDidUpdateEffect';
import { MobileMenuTabs, DesktopMenuTabs } from '../../components';
import QuotationTab from '../ViewPurchaseTransaction/QuotationTab';
import HandoverTab from './HandoverTab';
import TransactionTab from './TransactionTab';
import ValuationTab from './ValuationTab';
import VehicleInfo from './VehicleInfo';
import VehicleTab from './VehicleTab';
import type { PurchaseTransactionFormValues } from './index';

export type EditPurchaseTransactionBodyProps = {
    purchaseTransaction: FullPurchaseTransactionDataFragment;
    quotationUpdated?: QuotationUpdatedSubscription['quotationUpdated'];
    refetchPurchaseTransaction: GetPurchaseTransactionQueryResult['refetch'];
};

const a11yProps = (index: string) => ({
    id: `scrollable-force-tab-${index}`,
    'aria-controls': `scrollable-force-tabpanel-${index}`,
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(10),
                padding: theme.spacing(3),
                paddingRight: theme.spacing(4),
            },
        },
    })
);

const EditPurchaseTransactionBody = ({
    purchaseTransaction,
    quotationUpdated,
    refetchPurchaseTransaction,
}: EditPurchaseTransactionBodyProps) => {
    const styles = useStyles();
    const [value, setValue] = useState('transaction');
    const currentUser = useCurrentUser();
    const { setFieldValue } = useFormikContext<PurchaseTransactionFormValues>();

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const displayQuotation = [UserType.Approver, UserType.ValuationTeam, UserType.Admin].includes(currentUser.type);

    const quotationQuery: GetQuotationQueryResult = useGetQuotationQuery({
        fetchPolicy: 'cache-and-network',
        variables: { purchaseTransactionId: purchaseTransaction.id },
        skip: !displayQuotation,
    });

    const quotation = quotationQuery.data?.quotation;
    const { refetch: refetchQuotation } = quotationQuery;
    const liveDisplayQuery = useGetPurchaseTransactionLiveDisplayQuery({
        fetchPolicy: 'no-cache',
        variables: { purchaseTransactionId: purchaseTransaction.id },
        skip: !displayQuotation,
    });
    const liveQuotation = liveDisplayQuery.data?.quotation;
    const { refetch: refetchLiveDisplay } = liveDisplayQuery;
    useEffect(() => {
        if (quotationUpdated?.quotationEntryAdded) {
            Promise.all([refetchQuotation(), refetchLiveDisplay()]).catch(() => undefined);
        }
    }, [quotationUpdated, refetchLiveDisplay, refetchQuotation]);

    // Refetch purchase transaction when the valuation tab is active
    useEffect(() => {
        if (value === 'valuation') {
            refetchPurchaseTransaction({
                transactionId: purchaseTransaction.id,
            });
        }
    }, [purchaseTransaction.id, refetchPurchaseTransaction, value]);

    // Update dealer informations in the form when they change in the purchase transaction
    useDidUpdateEffect(() => {
        const dealerInformations = purchaseTransaction.valuation.dealerInformations ?? [];
        const sortedDealerInformations = [...dealerInformations].sort(
            (firstDealerInformation, secondDealerInformation) =>
                (secondDealerInformation.price ?? 0) - (firstDealerInformation.price ?? 0)
        );
        const dealerInformationSlots = [
            ...sortedDealerInformations,
            ...Array.from({ length: Math.max(0, maxDealerValuations - dealerInformations.length) }, () => ({
                name: '',
                price: null,
                isFromQuotation: false,
            })),
        ].slice(0, maxDealerValuations);

        setFieldValue('valuation.dealerInformations', dealerInformationSlots, false);
    }, [purchaseTransaction.valuation.dealerInformations, setFieldValue]);

    const submittedQuoteCount =
        (liveQuotation?.dealersInfo ?? quotation?.dealersInfo)?.filter(
            dealer => dealer.status === DealerQuotesStatus.Quoted
        ).length ?? 0;

    return (
        <>
            <Box className={styles.root} mb={isSmall ? 10 : 0}>
                <Grid spacing={2} container>
                    <Grid lg={5} md={5} sm={12} xs={12} item>
                        <VehicleInfo purchaseTransaction={purchaseTransaction} />
                    </Grid>
                    <Grid lg={7} md={7} sm={12} xs={12} item>
                        {!isSmall && (
                            <DesktopMenuTabs
                                a11yProps={a11yProps}
                                displayQuotation={displayQuotation}
                                setValue={setValue}
                                submittedQuoteCount={submittedQuoteCount}
                                value={value}
                            />
                        )}
                        <TabPanel index="transaction" value={value}>
                            <TransactionTab stage={purchaseTransaction.stage} />
                        </TabPanel>
                        <TabPanel index="vehicle" value={value}>
                            <VehicleTab stage={purchaseTransaction.stage} />
                        </TabPanel>
                        <TabPanel index="valuation" value={value}>
                            <ValuationTab />
                        </TabPanel>
                        {displayQuotation && (
                            <TabPanel index="quotation" value={value}>
                                <QuotationTab
                                    liveQuotation={liveQuotation}
                                    purchaseTransaction={purchaseTransaction}
                                    quotation={quotation}
                                    quotationLoading={quotationQuery.loading && !quotation}
                                    refetchLiveQuotation={refetchLiveDisplay}
                                    refetchQuotation={refetchQuotation}
                                />
                            </TabPanel>
                        )}
                        <TabPanel index="handover" value={value}>
                            <HandoverTab stage={purchaseTransaction.stage} />
                        </TabPanel>
                    </Grid>
                </Grid>
            </Box>
            {isSmall && (
                <MobileMenuTabs
                    a11yProps={a11yProps}
                    displayQuotation={displayQuotation}
                    setValue={setValue}
                    submittedQuoteCount={submittedQuoteCount}
                    value={value}
                />
            )}
        </>
    );
};

export default EditPurchaseTransactionBody;
